import { Component, EventEmitter, Input, Output, Renderer2 } from '@angular/core';
import { BaseViewComponent } from '@sb/library/base';

@Component({
    selector: 'lib-expandable-text',
    templateUrl: './expandable-text.component.html',
    styleUrl: './expandable-text.component.scss'
})
export class ExpandableTextComponent extends BaseViewComponent {

    @Input() targetText!: HTMLParagraphElement | HTMLPreElement;
    @Input() numberOfLines: number = 3;
    @Input() stopPropagation: boolean = true;

    @Output() toggle: EventEmitter<boolean> = new EventEmitter();

    private _isShowLess: boolean = false;
    private _currNumberOfLines: number = 0;
    private _isExpandable: boolean = false;
    private _fullHeight: number = 0;
    private _clampedHeight: number = 0;
    private _width: number = 0;

    private _changes?: MutationObserver;
    private _resizeObserver?: ResizeObserver;

    constructor(private _Renderer: Renderer2) {
        super();
    }

    override ngOnInit() {
        super.ngOnInit();
        this._changes = new MutationObserver(mutations => mutations.forEach(_ => this.RecomputeHeights()));
        this._changes.observe(this.targetText, { characterData: true, subtree: true });
        // Only apply these styles to pre elements: paragraph elements do not require these styles
        if (this.targetText instanceof HTMLPreElement) {
            this._Renderer.setStyle(this.targetText, "white-space", "pre-wrap");
            this._Renderer.setStyle(this.targetText, "word-break", "break-word");
            this._Renderer.setStyle(this.targetText, "margin-bottom", ".5rem");
            this._Renderer.setStyle(this.targetText, "margin-left", ".5rem");
        }
        this.RecomputeHeights();
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this._changes?.disconnect();
        this._resizeObserver?.disconnect();
    }

    ngAfterViewInit(): void {
        this.ObserveSizeChanges();
    }

    private RecomputeHeights() {
        this._Renderer.setStyle(this.targetText, 'transition', 'unset');
        this._Renderer.setStyle(this.targetText, 'height', 'unset');
        this._Renderer.setStyle(this.targetText, "overflow", "hidden");

        const lineHeight = parseInt(getComputedStyle(this.targetText).getPropertyValue("line-height"));
        if (Number.isNaN(lineHeight)) {
            // line height calculation returned "auto" - try again until it stabilizes to a value
            requestAnimationFrame(() => this.RecomputeHeights());
            return;
        }

        this._fullHeight = this.targetText.scrollHeight;
        // this._width = this.targetText.clientWidth;
        this._clampedHeight = this.numberOfLines * lineHeight;
        this._currNumberOfLines = lineHeight > 0 ? this._fullHeight / lineHeight : 0;

        this._isExpandable = this._fullHeight > this._clampedHeight;

        if (!this._isShowLess && this._isExpandable) {
            this.LineClampOnSetStyle();
        } else {
            this.LineClampOffSetStyle();
        }

        this._Renderer.setStyle(this.targetText, 'transition', 'height 0.2s ease-in-out');
    }

    ToggleShow(event: PointerEvent | MouseEvent): void {
        if (this.stopPropagation) {
            event.stopPropagation();
        }

        this._isShowLess = !this._isShowLess;

        if (this._isShowLess) {
            this.LineClampOffSetStyle();
        } else if (this._currNumberOfLines > this.numberOfLines) {
            this.LineClampOnSetStyle();
            if (this.targetText.getBoundingClientRect().top < 0) { // after collapsing, target text is outside the viewport from the top
                this.targetText.scrollIntoView({'behavior': 'smooth'});
            }
        }

        this.toggle.emit(this._isShowLess);
    }

    private LineClampOffSetStyle(): void {
        this._Renderer.setStyle(this.targetText, 'text-overflow', '');
        this._Renderer.setStyle(this.targetText, '-webkit-line-clamp', '');
        this._Renderer.setStyle(this.targetText, '-webkit-box-orient', '');
        this._Renderer.setStyle(this.targetText, 'display', '');
        this._Renderer.setStyle(this.targetText, 'height', this._fullHeight + 'px');
        // this._Renderer.setStyle(this.targetText, 'width', this._width + 'px');
    }

    private LineClampOnSetStyle(): void {
        // Line clamp produces an ellipsis after the text, do not remove. (also, line clamp alone
        // cannot be animated, so we use height as well to produce a smooth expand/collapse transition)
        this._Renderer.setStyle(this.targetText, 'text-overflow', 'ellipsis');
        this._Renderer.setStyle(this.targetText, '-webkit-line-clamp', `${this.numberOfLines}`);
        this._Renderer.setStyle(this.targetText, '-webkit-box-orient', 'vertical');
        this._Renderer.setStyle(this.targetText, 'display', '-webkit-box');
        this._Renderer.setStyle(this.targetText, 'height', this._clampedHeight + 'px');
        // this._Renderer.setStyle(this.targetText, 'width', this._width + 'px');
    }

    private ObserveSizeChanges(): void {
        this._resizeObserver = new ResizeObserver(entries => {
            requestAnimationFrame(() => { // to avoid the error of "Uncaught ResizeObserver loop completed with undelivered notifications."
                for (let entry of entries) {
                    const { width, height } = entry.contentRect;

                    if (width !== this._width) {
                        this._width = width;
                        this.RecomputeHeights();

                        // to make sure UI gets updated
                        this.DetectChanges();
                    }
                }
            });
        });

        this._resizeObserver.observe(this.targetText);
    }

    get isShowLess(): boolean {
        return this._isShowLess;
    }

    get isExpandable(): boolean {
        return this._isExpandable;
    }
}
